import { Button } from '@/components/ui/button'
import { ScrollArea, ScrollBar } from '@/components/ui/scroll-area'
import { Separator } from '@/components/ui/separator'
import { Event } from '@nostr/tools/wasm'
import { HugeiconsIcon } from '@hugeicons/react'
import { ReduceParagraphIcon, Router } from '@hugeicons/core-free-icons'
import { cn, emptyArray } from '@/lib/utils'
import { useRef, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  fetchEvent,
  getSeenEventRelayUrls,
  publishEventAndDisplay
} from '@/services/client.service'
import Note from './Note'
import Markdown from './Markdown'
import NoteStats from './NoteStats'
import BadgeAwardList from './BadgeAwardList'
import ForumTopicList from './ForumTopicList'
import ReactionList from './ReactionList'
import ReplyNoteList, { findAuthorChain } from './ReplyNoteList'
import RelaySelector, { Item, SelectableItem } from './RelaySelector'
import { loadRelayList } from '@nostr/gadgets/lists'
import { getRoot } from '@/lib/event'
import { repliesMapAtom } from '@/atoms/replies'
import {
  emptySummaryAtom,
  getSummary,
  getSummaryAtomIfExists,
  saveSummary
} from '@/atoms/summaries'
import { unique } from '@nostr/gadgets/utils'
import { useAsync } from 'react-use'
import { useAtom, useAtomValue } from 'jotai'
import { unwrappedFavoriteRelaysWithSetsAtom } from '@/atoms/lists'
import { canWriteAtom, unwrappedSignerAtom, useCurrentPubkey } from '@/atoms/session'
import { pool } from '@nostr/gadgets/global'
import * as kinds from '@nostr/tools/kinds'
import { getComposerStateAtom } from './PostEditor/composer-state'
import PostTextarea from './PostEditor/PostTextarea'
import { now } from '@/lib/utils'
import { hasPromptAPI } from '@/lib/prompt-api'

export default function NoteInteractions({
  pageIndex,
  event
}: {
  pageIndex?: number
  event: Event
}) {
  const { t } = useTranslation()
  const pubkey = useCurrentPubkey()
  const repliesMap = useAtomValue(repliesMapAtom)
  const favorites = useAtomValue(unwrappedFavoriteRelaysWithSetsAtom)
  const relaySelection = useRef<SelectableItem[]>()
  const seenOn = event.tags.some(([t]) => t === '-') ? getSeenEventRelayUrls(event.id, event) : []
  const forumTag = event.tags.find(([name]) => name === 'h')?.[1]
  const sourceRelay = getSeenEventRelayUrls(event.id, event)[0]
  const isForumTopic = event.kind === kinds.ForumThread && !!forumTag
  const isBadgeDefinition = event.kind === kinds.BadgeDefinition
  const groupId =
    event.kind === kinds.GroupMetadata ? event.tags.find(([name]) => name === 'd')?.[1] : undefined
  const isGroupMetadata = event.kind === kinds.GroupMetadata && !!groupId && !!sourceRelay
  const [type, setType] = useState<
    'comments' | 'replies' | 'reactions' | 'summary' | 'awards' | 'topics'
  >(
    isForumTopic
      ? 'replies'
      : isBadgeDefinition
        ? 'awards'
        : isGroupMetadata
          ? 'topics'
          : 'comments'
  )
  const forumReplyBoxRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!isForumTopic) return
    window.addEventListener('scroll-to-forum-reply-box', scrollToReplyBox)
    return () => window.removeEventListener('scroll-to-forum-reply-box', scrollToReplyBox)

    function scrollToReplyBox() {
      setType('replies')
      setTimeout(() => {
        forumReplyBoxRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' })
      }, 50)
    }
  }, [isForumTopic])

  const { value: forumComments } = useAsync(async () => {
    if (event.kind !== kinds.ForumThread || !forumTag || !sourceRelay) return []
    return pool
      .querySync([sourceRelay], {
        kinds: [kinds.Comment],
        '#h': [forumTag],
        '#E': [event.id]
      })
      .then((comments) =>
        comments.sort((a, b) => a.created_at - b.created_at || a.id.localeCompare(b.id))
      )
  }, [event, forumTag, sourceRelay])
  const [submittedForumComments, setSubmittedForumComments] = useState<Event[]>([])

  const allForumComments = [...(forumComments ?? []), ...submittedForumComments].filter(
    (comment, index, comments) => comments.findIndex((other) => other.id === comment.id) === index
  )
  const mainTabLabel =
    event.kind === kinds.ShortTextNote ||
    (event.kind === kinds.Comment &&
      event.tags.some(([name, value]) => name === 'K' && value === '1'))
      ? t('Replies')
      : t('Comments')

  const { value, loading } = useAsync(async () => {
    const relays: string[] = []

    const baseRelaysP = loadRelayList(event.pubkey)
    const pubkeyRelaysP = pubkey ? loadRelayList(pubkey) : Promise.resolve(undefined)
    const rootRelaysP = getRoot(event).then((rootPointer) =>
      rootPointer
        ? 'pubkey' in rootPointer
          ? loadRelayList(rootPointer.pubkey)
          : 'author' in rootPointer && rootPointer.author
            ? loadRelayList(rootPointer.author)
            : 'value' in rootPointer
              ? undefined
              : fetchEvent(rootPointer).then((root) =>
                  root ? loadRelayList(root.pubkey) : undefined
                )
        : undefined
    )

    for (const url of window.hallway.universe.readRepliesFrom ?? []) {
      relays.push(url)
    }
    for (const { url } of (await baseRelaysP).items.filter((url) => url.read)) {
      relays.push(url)
    }
    for (const { url } of (await rootRelaysP)?.items.filter((url) => url.read) ?? []) {
      relays.push(url)
    }

    const defaultRelays = new Set(relays)

    for (const { url } of (await pubkeyRelaysP)?.items.filter((url) => url.read) ?? []) {
      relays.push(url)
    }

    const ordered = unique(relays.concat(seenOn))
    const items: Item[] = ordered.map((url) => ({ kind: 'relay', url }))
    const added: string[] = [...ordered]

    {
      // favorites section
      items.push({ kind: 'separator' })
      const relaySetItems: Extract<Item, { kind: 'relay-set' }>[] = []
      for (const fav of favorites ?? []) {
        if (typeof fav === 'string') {
          if (added.includes(fav)) continue
          added.push(fav)
          items.push({ kind: 'relay', url: fav })
        } else {
          relaySetItems.push({
            kind: 'relay-set',
            identifier: fav.pointer.identifier,
            relays: fav.items,
            name: fav.title
          })
        }
      }

      if (relaySetItems.length) {
        if (items[items.length - 1].kind !== 'separator') items.push({ kind: 'separator' })
        items.push(...relaySetItems)
      }

      // if nothing was added remove the separator
      if (items[items.length - 1].kind === 'separator') items.pop()
    }

    return {
      relayOptions: items,
      initialSelection: window.hallway.universe.readRepliesFromExclusive
        ? new Set(window.hallway.universe.readRepliesFrom || [])
        : defaultRelays
    }
  }, [event, favorites])

  const [selectedRelays, setSelectedRelays] = useState<string[] | undefined>()

  const authorChain = useMemo(() => findAuthorChain(repliesMap, event), [repliesMap, event])

  const canUsePromptAPI = hasPromptAPI()
  const [summarizing, setSummarizing] = useState(false)
  const summary = useAtomValue(getSummaryAtomIfExists(event.id) ?? emptySummaryAtom)

  async function summarize() {
    setSummarizing(true)

    const summary = await getSummary(event)
    if (summary) {
      saveSummary(event.id, summary)
      setType('summary')
    }

    setSummarizing(false)
  }

  return (
    <>
      {authorChain.length > 0 && (
        <div className="divide-y">
          {authorChain.map((evt) => (
            <div key={evt.id} className="px-4 pt-3 pb-3">
              <Note event={evt} showFull hideParentNotePreview />
              <NoteStats className="mt-2" event={evt} />
            </div>
          ))}
        </div>
      )}
      <div className="flex items-center justify-between">
        <div className="flex-1 flex items-center gap-1 min-w-0">
          <ScrollArea className="flex-1 w-0">
            <InteractionTabs
              selectedTab={type}
              onTabChange={setType}
              hasSummary={summary !== undefined}
              hasForumReplies={isForumTopic}
              hasBadgeAwards={isBadgeDefinition}
              hasGroupTopics={isGroupMetadata}
              mainTabLabel={mainTabLabel}
            />
            <ScrollBar orientation="horizontal" className="opacity-0 pointer-events-none" />
          </ScrollArea>
        </div>
        <Separator orientation="vertical" className="h-6" />
        <div className="flex items-center">
          {canUsePromptAPI && (
            <div className="size-10 flex items-center justify-center">
              <Button
                variant="ghost"
                size="icon"
                title={t('Summarize thread')}
                disabled={summarizing}
                onClick={summarize}
              >
                <HugeiconsIcon
                  icon={ReduceParagraphIcon}
                  className={cn('text-muted-foreground', summarizing && 'animate-pulse')}
                />
              </Button>
            </div>
          )}
          {!loading && (
            <RelaySelector
              selected={value?.initialSelection || new Set()}
              onChange={(sel) => {
                relaySelection.current = sel
              }}
              onClose={() => {
                if (relaySelection.current)
                  setSelectedRelays(
                    relaySelection.current?.flatMap?.((item) =>
                      item.kind === 'relay'
                        ? item.url
                        : item.kind === 'relay-set'
                          ? item.relays
                          : []
                    ) || []
                  )
              }}
              items={value?.relayOptions || emptyArray}
            >
              <div className="size-10 flex items-center justify-center">
                <Button variant="ghost" size="icon" title={t('Relays')}>
                  <HugeiconsIcon icon={Router} className="text-muted-foreground" />
                </Button>
              </div>
            </RelaySelector>
          )}
        </div>
      </div>
      <div className={type === 'summary' ? '' : 'hidden'}>
        {summary ? (
          <div className="px-4 py-3">
            <Markdown content={summary} event={event} />
          </div>
        ) : (
          <div className="text-sm py-8 text-center text-muted-foreground">
            {t('No summary yet')}
          </div>
        )}
      </div>
      {isForumTopic && (
        <div className={type === 'replies' ? '' : 'hidden'}>
          <div className="divide-y">
            {allForumComments.map((comment) => (
              <div key={comment.id} className="px-4 py-3">
                <Note event={comment} hideParentNotePreview />
              </div>
            ))}
          </div>
          <ForumReplyEditor
            hTag={forumTag}
            rootId={event.id}
            relay={sourceRelay}
            onSubmitted={(comment) => setSubmittedForumComments((current) => [...current, comment])}
          />
        </div>
      )}
      <div ref={forumReplyBoxRef} />
      <div className={type === 'comments' ? '' : 'hidden'}>
        <ReplyNoteList index={pageIndex} event={event} selectedRelays={selectedRelays} />
      </div>
      <div className={type === 'reactions' ? '' : 'hidden'}>
        <ReactionList event={event} selectedRelays={selectedRelays} />
      </div>
      {isBadgeDefinition && (
        <div className={type === 'awards' ? '' : 'hidden'}>
          <BadgeAwardList event={event} selectedRelays={selectedRelays} />
        </div>
      )}
      {isGroupMetadata && (
        <div className={type === 'topics' ? '' : 'hidden'}>
          <ForumTopicList event={event} />
        </div>
      )}
    </>
  )
}

function InteractionTabs({
  selectedTab,
  onTabChange,
  hasSummary,
  hasForumReplies,
  hasBadgeAwards,
  hasGroupTopics,
  mainTabLabel
}: {
  selectedTab: 'comments' | 'replies' | 'reactions' | 'summary' | 'awards' | 'topics'
  onTabChange: (tab: 'comments' | 'replies' | 'reactions' | 'summary' | 'awards' | 'topics') => void
  hasSummary?: boolean
  hasForumReplies?: boolean
  hasBadgeAwards?: boolean
  hasGroupTopics?: boolean
  mainTabLabel: string
}) {
  const { t } = useTranslation()
  const tabRefs = useRef<(HTMLDivElement | null)[]>([])
  const [indicatorStyle, setIndicatorStyle] = useState({ width: 0, left: 0 })
  const tabs: {
    key: 'comments' | 'replies' | 'reactions' | 'summary' | 'awards' | 'topics'
    label: string
  }[] = [
    ...(hasForumReplies ? [{ key: 'replies' as const, label: 'Replies' }] : []),
    ...(hasBadgeAwards ? [{ key: 'awards' as const, label: 'Awards' }] : []),
    ...(hasGroupTopics ? [{ key: 'topics' as const, label: 'Topics' }] : []),
    { key: 'comments', label: mainTabLabel },
    { key: 'reactions' as const, label: 'Reactions' },
    ...(hasSummary ? [{ key: 'summary' as const, label: 'Summary' }] : [])
  ]

  useEffect(() => {
    setTimeout(() => {
      const activeIndex = tabs.findIndex((tab) => tab.key === selectedTab)
      if (activeIndex >= 0 && tabRefs.current[activeIndex]) {
        const activeTab = tabRefs.current[activeIndex]
        const { offsetWidth, offsetLeft } = activeTab
        const padding = 32 // 16px padding on each side
        setIndicatorStyle({
          width: offsetWidth - padding,
          left: offsetLeft + padding / 2
        })
      }
    }, 20) // ensure tabs are rendered before calculating
  }, [selectedTab, hasSummary, hasForumReplies, hasBadgeAwards, hasGroupTopics, mainTabLabel])

  return (
    <div className="w-fit">
      <div className="flex relative">
        {tabs.map((tab, i) => (
          <div
            key={tab.key}
            ref={(el) => (tabRefs.current[i] = el)}
            className={cn(
              `text-center px-4 py-2 font-semibold transition-colors hover:bg-muted/30 active:bg-muted/30 cursor-pointer rounded-lg`,
              selectedTab === tab.key ? '' : 'text-muted-foreground'
            )}
            onClick={() => onTabChange(tab.key)}
          >
            {t(tab.label)}
          </div>
        ))}
        <div
          className="absolute bottom-0 h-1 bg-primary rounded-full transition-all duration-500"
          style={{
            width: `${indicatorStyle.width}px`,
            left: `${indicatorStyle.left}px`
          }}
        />
      </div>
    </div>
  )
}

function ForumReplyEditor({
  hTag,
  rootId,
  relay,
  onSubmitted,
  ref
}: {
  hTag: string
  rootId: string
  relay?: string
  onSubmitted: (comment: Event) => void
  ref?: React.Ref<HTMLDivElement>
}) {
  const { t } = useTranslation()
  const canWrite = useAtomValue(canWriteAtom)
  const signer = useAtomValue(unwrappedSignerAtom)
  const [text, setText] = useState('')
  const [posting, setPosting] = useState(false)
  const [editorKey, setEditorKey] = useState(0)
  const composerStateAtom = getComposerStateAtom(
    '',
    undefined,
    relay ? [relay] : undefined,
    () => ({
      nsfw: false,
      quietReply: false,
      protected: false,
      premium: false,
      relaySelection: relay ? [{ kind: 'relay', url: relay }] : [],
      content: null
    })
  )
  const [, setComposerState] = useAtom(composerStateAtom)

  // slash commands in the reply box target a kind:1111 comment on this thread,
  // published only to this relay, so `relay:` scoped commands must include it
  const pubkey = useCurrentPubkey()
  const slashComposerContext = useMemo(
    () => ({
      kind: kinds.Comment,
      tags: [
        ['e', rootId],
        ['h', hTag]
      ],
      pubkey: pubkey ?? undefined,
      relays: relay ? [relay] : []
    }),
    [rootId, hTag, relay, pubkey]
  )

  async function submit() {
    if (!canWrite || !signer || !relay || !text.trim() || posting) return
    setPosting(true)
    try {
      const comment = await signer.signEvent({
        kind: kinds.Comment,
        content: text,
        tags: [
          ['E', rootId, relay],
          ['e', rootId, relay],
          ['h', hTag]
        ],
        created_at: now()
      })
      await publishEventAndDisplay([relay], comment)
      onSubmitted(comment)
      setText('')
      setComposerState((current) => ({ ...current, content: null }))
      setEditorKey((current) => current + 1)
    } finally {
      setPosting(false)
    }
  }

  return (
    <div ref={ref} className="space-y-2 border-t px-4 py-3">
      <PostTextarea
        key={editorKey}
        text={text}
        setText={setText}
        className="min-h-52"
        defaultContent=""
        composerStateAtom={composerStateAtom}
        onSubmit={submit}
        slashComposerContext={slashComposerContext}
      />
      <div className="flex justify-end">
        <Button
          size="lg"
          disabled={!canWrite || !relay || !text.trim() || posting}
          onClick={submit}
        >
          {posting ? t('Submitting...') : t('Submit')}
        </Button>
      </div>
    </div>
  )
}
